/**
 * The claim that follows a "Sign in" the reader took from a signed-out write's own offer — the
 * feed's Ask ("Get the answer by email", 8930b683 / 64518158) and the feed vote's toast ("Counted.
 * Sign in to keep your votes.", 2026-10-18). One module, both callers: one marker, one page-wide
 * guard, one claim per armed sign-in.
 *
 * SG's sign-in never claims a device by itself (#81). The reader's click on the offer is the
 * consent; the claim (`attemptLink` over `ownerApi.claim`, the band's arm-then-claim path) runs
 * once the session token that click led to arrives — on this page, or on the next one within
 * `CLAIM_PENDING_MS` (an email-link sign-in lands in another tab or after a reload). The claim
 * makes this browser's device the member's: its questions (contract #209) and its votes (DealHunt
 * folds them into the member inside the claim's transaction, 5670520d) become the member's.
 *
 * Client-only: localStorage and module state, read in effects — nothing reaches the shared HTML.
 */
import { useEffect, useRef, useState } from 'react'
import { ownerApi } from './api'
import type { OwnerCredential } from './auth-bridge'
import { hasDeviceMarker } from './declared'
import { attemptLink, type LinkOutcome } from './link'

/** The offer's click, remembered across a reload or a new tab. The key keeps its Ask-era name:
 *  renaming it would drop the markers in flight at deploy. Written on the click, cleared when the
 *  claim runs. */
export const CLAIM_PENDING_KEY = 'sg:owner-ask:claim-pending'
/** How long the click consents to a claim (64518158's window, Constraint: never on an unrelated
 *  sign-in outside it). */
export const CLAIM_PENDING_MS = 30 * 60 * 1000

function markClaimPending(): void {
  try {
    window.localStorage.setItem(CLAIM_PENDING_KEY, String(Date.now()))
  } catch {
    // Storage off: the claim still runs if the sign-in lands on this page (the in-page arm).
  }
}

function readPending(): boolean {
  const at = Number(window.localStorage.getItem(CLAIM_PENDING_KEY))
  return Number.isFinite(at) && at > 0 && Date.now() - at < CLAIM_PENDING_MS
}

/** The marker is set and inside the window; consumes it. */
export function takeClaimPending(): boolean {
  try {
    const due = readPending()
    window.localStorage.removeItem(CLAIM_PENDING_KEY)
    return due
  } catch {
    return false
  }
}

/** The marker is set and inside the window; leaves it. */
export function claimPending(): boolean {
  try {
    return readPending()
  } catch {
    return false
  }
}

/** One claim per page at a time. */
let claimRunning = false
/** An offer showed on this page: only the caller whose offer was clicked runs the claim (with its
 *  own outcome line) — no other mounted caller claims silently on a marker it did not write. */
let offerShownHere = false

/** A caller showed its sign-in offer (the Ask's "Get the answer by email", the vote's toast). */
export function markOfferShown(): void {
  offerShownHere = true
}

/**
 * Arm-then-claim for one caller. `arm()` is the offer's click (before `showLogIn`): it writes the
 * marker and remembers the token it was clicked under. When a NEW token arrives — or, with no
 * offer shown on this page, a marker another page wrote is due — the claim runs once, page-wide.
 * `onOutcome` hears only the claim this caller armed; a carried claim is silent.
 */
export function useClaimAfterSignIn(
  credential: OwnerCredential | null,
  onOutcome: (outcome: LinkOutcome) => void
): { armed: boolean; arm: () => void } {
  const [armed, setArmed] = useState(false)
  const armedTokenRef = useRef<string | null>(null)
  const onOutcomeRef = useRef(onOutcome)
  onOutcomeRef.current = onOutcome

  useEffect(() => {
    if (!credential || claimRunning || !hasDeviceMarker()) {
      return
    }
    const armedHere = armed && credential.token !== armedTokenRef.current
    // Another page carried the offer here: the first caller claims, silently.
    const carried = !offerShownHere && !armed && claimPending()
    if (!armedHere && !carried) {
      return
    }
    if (!takeClaimPending() && !armedHere) {
      return
    }
    claimRunning = true
    setArmed(false)
    void attemptLink(ownerApi.claim, credential)
      .then(outcome => {
        if (armedHere) {
          onOutcomeRef.current(outcome)
        }
      })
      .finally(() => {
        claimRunning = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one claim per armed sign-in
  }, [armed, credential?.token])

  const arm = (): void => {
    armedTokenRef.current = credential?.token ?? null
    offerShownHere = true
    markClaimPending()
    setArmed(true)
  }

  return { armed, arm }
}
